import React from "react";
import "./HistoricalPerformanceChart.css";

export const HISTORICAL_VIEW_RANGES = Object.freeze(["L5", "L10", "L20", "H2H", "SEASON"]);

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export default function HistoricalPerformanceChart({
  games = [],
  line,
  range = "L5",
  period = "FULL_GAME",
  formatValue = (value) => String(value),
  darkMode = false,
  maxPoints = 5,
  recommendation = "OVER",
  comparisonColors = "recommendation",
}) {
  const boundedMax = Number.isInteger(maxPoints) && maxPoints > 0 ? Math.min(maxPoints, 10) : 5;
  const points = (Array.isArray(games) ? games : []).slice(-boundedMax)
    .map((game, index) => ({ ...game, value: finiteNumber(game?.value), sourceIndex: index }))
    .filter((game) => game.value !== null);
  if (!points.length) return null;

  const reference = finiteNumber(line);
  const values = points.map(({ value }) => value);
  if (reference !== null) values.push(reference);
  const minimum = Math.min(0, ...values);
  const maximum = Math.max(0, ...values);
  const span = maximum - minimum || 1;
  const lineBottom = reference === null ? null : ((reference - minimum) / span) * 100;

  return (
    <div
      className="pwg-recent-form-chart"
      data-testid="historical-performance-chart"
      data-range={range}
      data-period={period}
      role="group"
      aria-label={boundedMax === 5 ? "Five most recent results in chronological order; newest result on the right." : `${points.length} most recent results in chronological order; newest result on the right.`}
      style={{ marginTop: 0, overflowX: "auto", overflowY: "hidden" }}
    >
      <div style={{ minWidth: Math.max(364, points.length * 70 + 64) }}>
        <div className="pwg-recent-form-plot" style={{ position: "relative", display: "grid", gridTemplateColumns: `repeat(${points.length}, minmax(54px, 1fr))`, gap: 10, alignItems: "end", borderBottom: darkMode ? "1px solid #475569" : "1px solid #cbd5e1", paddingRight: 64, boxSizing: "border-box" }}>
        {lineBottom !== null && (
          <div
            aria-label={`Current prop line ${formatValue(reference)}`}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `${Math.max(0, Math.min(100, lineBottom))}%`,
              borderTop: `2px dashed ${darkMode ? "#fbbf24" : "#d97706"}`,
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            <span style={{ position: "absolute", right: 4, top: -12, color: darkMode ? "#fcd34d" : "#92400e", background: darkMode ? "#0f172a" : "#fff7ed", border: darkMode ? "1px solid #92400e" : "1px solid #fed7aa", borderRadius: 999, padding: "2px 6px", boxShadow: `0 0 0 3px ${darkMode ? "#0f172a" : "#fff"}`, fontSize: 10, lineHeight: 1.2, fontWeight: 900, whiteSpace: "nowrap", zIndex: 3 }}>
              Line {formatValue(reference)}
            </span>
          </div>
        )}

          {points.map((game, index) => {
            const height = Math.max(0, Math.min(100, ((game.value - minimum) / span) * 100));
            const result = reference === null ? "UNKNOWN" : game.value > reference ? "OVER" : game.value < reference ? "UNDER" : "PUSH";
            const direction = String(recommendation || "").toUpperCase();
            const favorable = direction === "UNDER" ? result === "UNDER" : direction === "OVER" ? result === "OVER" : false;
            const unfavorable = direction === "UNDER" ? result === "OVER" : direction === "OVER" ? result === "UNDER" : false;
            const color = comparisonColors === "absolute"
              ? result === "OVER" ? "#16a34a" : result === "UNDER" ? "#dc2626" : "#64748b"
              : result === "PUSH" || (!favorable && !unfavorable) ? "#64748b" : favorable ? "#16a34a" : "#dc2626";
            return (
              <div key={game.key || `${game.dateLabel || "game"}-${index}`} title={game.title || ""} style={{ height: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", minWidth: 0 }}>
                <div aria-label={`${game.dateLabel || `Game ${index + 1}`} ${result} ${formatValue(game.value)}`} style={{ width: "min(38px, 72%)", height: `${height}%`, minHeight: 18, borderRadius: "6px 6px 2px 2px", background: color, display: "flex", alignItems: "flex-start", justifyContent: "center", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.22)", position: "relative", zIndex: 1 }}>
                  <span style={{ marginTop: 4, color: "#fff", fontSize: 11, lineHeight: 1, fontWeight: 950, textShadow: "0 1px 2px rgba(0,0,0,.35)" }}>{formatValue(game.value)}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ minHeight: 44, display: "grid", gridTemplateColumns: `repeat(${points.length}, minmax(54px, 1fr))`, gap: 10, paddingTop: 6, paddingRight: 64, boxSizing: "border-box" }}>
          {points.map((game, index) => (
            <div key={`label-${game.key || index}`} style={{ minWidth: 0, textAlign: "center", color: darkMode ? "#cbd5e1" : "#475569", fontSize: 10, lineHeight: 1.25 }}>
              <div style={{ fontWeight: 900, whiteSpace: "nowrap" }}>{game.dateLabel || `Game ${index + 1}`}</div>
              <div style={{ marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={game.opponentLabel || ""}>{game.opponentLabel || "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
